import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {
  Badge,
  badgeStyle,
  badgeType,
  Breadcrumbs,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Icons,
} from '../../../packages';
import { reportingActionCreators } from '../../../redux/modules/reporting';
import { selectClaim, selectProviderFee } from '../../../redux/modules/reporting/selectors';
import { showSnackbar } from '../../../redux/modules/snackbar';
import { stateActionCreators } from '../../../redux/modules/state';
import { selectStatePayer, selectStatePayers } from '../../../redux/modules/state/selectors';
import { BaseLayout } from '../../../layouts/base';
import { useStyles } from './ClaimDetail.styles';
import Submitter from './components/Submitter';
import Patient from './components/Patient';
import Provider from './components/Provider';
import ClaimStatus from './components/ClaimStatus';
import CreateInvoice from './components/CreateInvoice';
import ClaimNote from './components/ClaimNote';
import Insurance from './components/Insurance';
import { profileActionCreators } from '../../../redux/modules/profile';
import { getProfile, selectMemberInsurance } from '../../../redux/modules/profile/selectors';
import { validateInsuranceInfo } from '../../../services/member/member.service';
import { getStatesSimple } from '../../../services/state/state.service';
import InsuranceInformationEdit from '../../provider/member-detail/components/insurance-information/edit';
import clsx from 'clsx';
import { reSubmitClaim } from '../../../services/report-service/reportService.service';

const ClaimDetail = () => {
  const classes = useStyles();
  const location = useLocation();
  const state = location?.state;
  const dispatch = useDispatch();
  const { claimId } = useParams();
  const { demographicDetails } = useSelector(getProfile);

  useEffect(() => {
    dispatch(stateActionCreators.fetchPayers('unique'));
    dispatch(reportingActionCreators.fetchClaimById({ id: claimId }));
    dispatch(stateActionCreators.fetchCPTs());
  }, []);

  const { isLoading, isSaving, errorMsg, data } = useSelector(selectClaim);
  const { providerFeeData } = useSelector(selectProviderFee);
  const [currentProviderFeeDtata, setCurrentProviderFeeDtata] = useState(providerFeeData);
  const { insurance } = useSelector(selectMemberInsurance);
  const payer = useSelector(selectStatePayer);
  const [statesSimpleList, setStatesSimpleList] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openInsuranceEdit, setOpenInsuranceEdit] = useState(false);
  const getAllStates = async () => {
    try {
      const response = await getStatesSimple();
      setStatesSimpleList(response?.data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    setCurrentProviderFeeDtata(providerFeeData);
  }, [providerFeeData]);
  useEffect(() => {
    if (data?.payerRef) {
      dispatch(stateActionCreators.fetchPayerDetail(data?.payerRef));
    }
  }, [data?.payerRef]);

  useEffect(() => {
    if (data?.appointmentId) {
      dispatch(reportingActionCreators.fetchProviderFeeByAppointmentId({ id: data?.appointmentId }));
    }
  }, [data?.appointmentId]);

  useEffect(() => {
    if (data?.patient) {
      dispatch(profileActionCreators.fetchMemberInsurance({ memberId: data.patient?.userAccountId }));
      dispatch(profileActionCreators.fetchDemographicDetails(data.patient?.userAccountId));
      void getAllStates();
    }
  }, [data?.patient]);

  const onVerifyInsurance = () => {
    validateInsuranceInfo(data.patient?.userAccountId)
      .then(res => {
        if (!res.data?.success) {
          dispatch(
            showSnackbar({ snackType: 'error', snackMessage: res.data?.message || 'Something went wrong' }),
          );
          return;
        }
        dispatch(profileActionCreators.fetchMemberInsurance({ memberId: data.patient?.userAccountId }));
        dispatch(showSnackbar({ snackType: 'success', snackMessage: res.data?.message }));
      })
      .catch(error => {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: error.message || `Unable to validate insurance information.`,
          }),
        );
        dispatch(profileActionCreators.fetchMemberInsurance({ memberId: data.patient?.userAccountId }));
      });
  };

  const onSubmit = useCallback((formData, type) => {
    if (type === 'Provider_Fee') {
      dispatch(
        reportingActionCreators.updateProviderFeeByProviderId({
          formData: {
            cptList: currentProviderFeeDtata?.cptList,
            insuranceDetail: {
              isActive: formData?.isActive,
              payer: formData?.payer,
              plan: formData?.plan,
            },
            amount: formData?.amount,
          },
          providerId: currentProviderFeeDtata?._id,
        }),
      );
      dispatch(reportingActionCreators.fetchProviderFeeByAppointmentId({ id: state?.appointmentId }));
    } else {
      dispatch(
        reportingActionCreators.updateClaimDetail({
          type,
          formData: {
            [type]: { ...data[type], ...formData },
            billingProviderRef: data?.billingProviderRef,
            renderingProviderRef: data?.renderingProviderRef,
            submitterRef: data?.submitterRef,
            payerRef: data?.payerRef,
            patientRef: data?.patientRef,
          },
          claimId,
        }),
      );
    }
    setTimeout(() => {
      if (errorMsg) {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: errorMsg || 'Something went wrong!',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Record updated successfully',
          }),
        );
      }
    }, 1000);
  }, []);

  const handleReSubmit = async () => {
    try {
      const response = await reSubmitClaim({ apptId: data?.appointmentId }, { resubmitClaimRequest: true });
      if (!response.data?.success) {
        dispatch(showSnackbar({ snackType: 'error', snackMessage: response.data?.message }));
        return;
      }
      dispatch(showSnackbar({ snackType: 'success', snackMessage: 'Claim re-submitted successfully' }));
      dispatch(reportingActionCreators.fetchClaimById({ id: claimId }));
    } catch (error) {
      dispatch(showSnackbar({ snackType: 'error', snackMessage: 'Failed to re-submit claim' }));
      console.log(error);
    }
  };

  return (
    <BaseLayout>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Breadcrumbs
            links={[
              { href: '/admin/claims', text: 'Claims' },
              {
                href: `/admin/members/${data?.patient?.userAccountId}?name=dashboard`,
                text: `${data?.patient?.firstName || ''} ${data?.patient?.lastName || ''}`,
              },
              { text: data?.name || 'N/A' },
            ]}
          />
        </Box>
        {isLoading || isSaving ? (
          <Box
            display="flex"
            justifyContent="center"
            height={'calc(100vh - 88px)'}
            alignItems="center"
            sx={{ paddingY: 5 }}
          >
            <Icons
              className={clsx('rotate linear infinite', classes.bigLogo)}
              glyph="in-progress"
              color={colors.primary}
              height={80}
              width={80}
            />
          </Box>
        ) : (
          <>
            <Stack py={5} pt={10} direction="column">
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
                    {data?.name || 'N/A'}
                  </Heading>
                  <Badge variant={badgeType.FILLED} style={badgeStyle.ACTIVE} size="28px">
                    Completed
                  </Badge>
                </Stack>
                <Stack gap={1} direction="row">
                  {data?.statusDetail?.claimStatus === 'HELD' ? (
                    <Button variant={btnType.PRIMARY} onClick={handleReSubmit}>
                      Submit
                    </Button>
                  ) : (
                    <>
                      {data?.candidStatus === 'CANDID_FAILURE' && (
                        <Button variant={btnType.PRIMARY} onClick={handleReSubmit}>
                          Re-Submit
                        </Button>
                      )}
                      <Button variant={btnType.PRIMARY} onClick={() => setOpenInvoice(true)}>
                        Create Invoice
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
              <Heading className={classes.subHeading} level={headingLevel.S} weight={fontWeight.REGULAR}>
                {dayjs(data?.updatedAt || data?.createdAt).format('MM/DD/YYYY, hh:mm a')}
              </Heading>
            </Stack>
            <Stack direction="column" gap={2}>
              <ClaimStatus
                data={{
                  ...data?.statusDetail,
                  candidStatus: data?.candidStatus,
                  candidErrorDetails: data?.candidErrorDetails,
                  dateOfService: data?.appointmentStartTime || '',
                  cptCodes: data?.claim?.cptList?.map(c => c.cptCode) || [],
                  icd10Code: data?.claim?.cptList?.find(c => c.icd10Code)?.icd10Code || '',
                  updatedAt: data?.updatedAt,
                  amountBilled: (data?.claim?.totalClaim && `$${data?.claim?.totalClaim}`) || '',
                  trackingNumber: data?.candidSyncDetails?.candidResponse?.payer_claim_id,
                  payerName: data?.payer?.name,
                  invoice: data?.invoice,
                  syncDetails: data?.candidSyncDetails,
                  candidResponse: data?.candidSyncDetails?.candidResponse,
                  externalClaimId: data?.candidSyncDetails?.candidResponse?.claim_id,
                  status: data?.status,
                }}
                onSubmit={onSubmit}
              />
              <ClaimNote data={{ notes: data?.statusDetail?.notes }} onSubmit={onSubmit} />
              <Patient
                data={{
                  ...data?.patient,
                  externalPatientId: data?.candidSyncDetails?.candidFullResponse?.patient?.individual_id,
                  externalPatientUrl: null,
                  demographicDetails: demographicDetails,
                }}
              />
              <Insurance
                data={{ ...data, insurance: insurance }}
                onValidateInsurance={onVerifyInsurance}
                onEditInsurance={() => setOpenInsuranceEdit(true)}
              />
              <Provider
                data={{
                  ...data?.renderingProvider,
                  organizationName: data?.submitter?.name,
                  organizationState: data?.submitter?.state,
                  serviceLocation: data?.candidSyncDetails?.candidFullResponse?.service_facility,
                  taxonomyCode:
                    data?.candidSyncDetails?.candidFullResponse?.rendering_provider?.taxonomy_code,
                }}
              />
              <Submitter
                data={{
                  ...data?.submitter,
                  candidBillingProvider: data?.candidSyncDetails?.candidFullResponse?.billing_provider,
                }}
                onSubmit={onSubmit}
              />
            </Stack>
          </>
        )}
        {openInvoice && (
          <CreateInvoice payerObj={payer} open={openInvoice} onClose={() => setOpenInvoice(false)} />
        )}
        {openInsuranceEdit && (
          <InsuranceInformationEdit
            openDrawer={openInsuranceEdit}
            onClose={() => setOpenInsuranceEdit(false)}
            data={insurance}
            states={statesSimpleList}
            vbc={demographicDetails?.vbcFlag}
            patientInfo={demographicDetails}
          />
        )}
      </Box>
    </BaseLayout>
  );
};

export { ClaimDetail };
